/**
 * Talking to the drawing service, and everything that has to be true first.
 *
 * Both pages draw. The workbench draws all day, and the welcome page draws once
 * to prove it can, which means the bot check, the token dance, the request
 * shape and the shelf all have two callers. Every one of those is a place where
 * two copies would drift apart quietly: a welcome page that posted a slightly
 * older request shape would keep working right up until the day it did not, and
 * the failure would look like the service being broken rather than the page
 * being stale.
 *
 * So the network and the storage live here, and the pages own only their own
 * pixels.
 */

import { renderFrames } from "../src/render.js";
import { SseReader } from "../src/sse.js";
import type { SpriteSpec } from "../src/spec.js";
import type { StreamEvent } from "../src/stream.js";
import type { Motion, View } from "../src/ask.js";
import { counted } from "./stats.js";

// ---- the bot check --------------------------------------------------------------

/**
 * Turnstile, if it has been switched on.
 *
 * The page asks the server what it needs rather than being rebuilt: adding the
 * keys turns the widget on with no deploy of the site.
 *
 * The token is single use, so it is taken and reset on each draw and a fresh
 * one requested. Drawing twice without that gets a rejection on the second.
 */
declare const turnstile:
	| { render: (el: HTMLElement, opts: Record<string, unknown>) => string; reset: (id: string) => void }
	| undefined;

let botToken: string | null = null;
let widgetId: string | null = null;

/**
 * Off entirely, waiting for the script, or working.
 *
 * There is no "broken" here any more, and its absence is the point. It used to
 * exist and be set from the widget's error callback, which latched: Turnstile
 * reports transient things there, including a network blip, an expired
 * challenge, and several resets in quick succession, and the first of those
 * turned drawing off for the rest of the session. Somebody drawing four sprites
 * in a row hit it every time, and the page told them to check their privacy
 * extensions while a perfectly good token sat in the DOM beside the message.
 *
 * A failure to LOAD the script is still fatal and still recorded, because
 * without it no token can ever be issued. A failure from a loaded widget is
 * not: it is retried, and the only thing that decides a draw is whether a token
 * turns up when one is asked for.
 */
export type BotState = "off" | "loading" | "ready" | "unavailable";
let botState: BotState = "off";

export const botStatus = () => botState;

/** The script never loaded. Nothing will work until that is fixed. */
export const BOT_BLOCKED =
	"The bot check could not load, so drawing is switched off. It is usually a privacy extension or a blocked script. Allow challenges.cloudflare.com and reload.";

/**
 * The script loaded but no token arrived in time.
 *
 * Two different situations, and a visitor cannot tell them apart, so the
 * message covers both: the widget may be sitting there waiting to be clicked,
 * or it may have errored and be retrying. Either way pressing Draw again is the
 * right next move, which is what this says.
 */
export const BOT_WAITING =
	"The bot check has not finished yet. If a Cloudflare box has appeared, complete it and press Draw again.";

const ON_LOAD = "bitwrightTurnstileReady";

/**
 * @param into the element the widget mounts in. Its id must not be `turnstile`:
 * browsers publish element ids as globals, and that one shadows the script's
 * own object and stops it ever starting.
 */
/**
 * What the server says the rules are.
 *
 * Allowances are read rather than written into the page, because they move
 * while accounts are being built and a hand-written "one free sprite" in the
 * hero was wrong within a day of being written. One fetch, shared, so asking
 * twice costs nothing.
 */
export interface Config {
	turnstile: string | null;
	anonLimit: number;
	freeLimit: number;
	accountRequired: boolean;
}

let asked: Promise<Config> | null = null;

export function config(): Promise<Config> {
	asked ??= fetch("/api/config")
		.then((r) => r.json() as Promise<Config>)
		.catch(() => ({ turnstile: null, anonLimit: 1, freeLimit: 10, accountRequired: false }));
	return asked;
}

export async function setUpBotCheck(into: HTMLElement | null) {
	if (!into) return;
	try {
		const { turnstile: siteKey } = await config();
		if (!siteKey) return;
		botState = "loading";

		// The script's own onload parameter, not the tag's.
		//
		// Turnstile publishes `window.turnstile` as a stub before its API exists,
		// so a plain `script.onload` resolves while `turnstile.render` is still
		// undefined. That failure is silent and, with the endpoint refusing
		// tokenless draws, it takes the whole site down with it.
		await new Promise<void>((resolve, reject) => {
			(window as unknown as Record<string, unknown>)[ON_LOAD] = () => resolve();
			const tag = document.createElement("script");
			tag.src = `https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=${ON_LOAD}`;
			tag.async = true;
			tag.onerror = () => reject(new Error("the bot check could not be loaded"));
			document.head.append(tag);
			setTimeout(() => reject(new Error("the bot check timed out")), 15000);
		});
		if (typeof turnstile === "undefined" || typeof turnstile.render !== "function") {
			throw new Error("the bot check loaded but did not start");
		}

		widgetId = turnstile.render(into, {
			sitekey: siteKey,
			theme: "dark",
			// Show the widget only when it actually wants something.
			//
			// The default draws a Cloudflare-branded box for everyone, which on a
			// workbench built to fit one screen is a permanent block of somebody
			// else's branding explaining a check that passed silently.
			// `interaction-only` keeps it at zero height until a visitor is
			// challenged, and then it appears and says why.
			appearance: "interaction-only",
			callback: (token: string) => {
				botToken = token;
			},
			"expired-callback": () => {
				botToken = null;
				rearm();
			},
			// Returning true says the page is handling this rather than letting
			// Turnstile draw its own error state. Handling it means trying again,
			// because almost everything reported here is temporary.
			"error-callback": () => {
				botToken = null;
				rearm();
				return true;
			},
		});
		botState = "ready";
	} catch {
		// The script itself never started, so no token can ever be issued. That is
		// the one genuinely fatal case, and it is recorded so the page can say so
		// plainly instead of letting somebody press Draw and collect a 403 they
		// cannot act on.
		botState = "unavailable";
	}
}

/**
 * Ask the widget for another token.
 *
 * Debounced, because the situations that call for it arrive in bursts: an error
 * callback can fire several times, and resetting on each one is itself
 * something Turnstile will start refusing.
 */
let rearming = 0;
function rearm(delay = 400) {
	if (rearming || !widgetId || typeof turnstile === "undefined") return;
	rearming = window.setTimeout(() => {
		rearming = 0;
		try {
			if (widgetId) turnstile.reset(widgetId);
		} catch {
			// Resetting a widget that is mid-solve throws. It will finish on its own.
		}
	}, delay);
}

/**
 * Wait for a token rather than grabbing whatever is there.
 *
 * A managed widget usually solves itself, but not instantly, so reading the
 * token the moment Draw is pressed fails on the first attempt and works on the
 * second. One is single use, so it is cleared and a fresh one requested for
 * next time.
 *
 * Nothing here consults a stored health flag. Whether a draw can proceed is
 * decided by whether a token turns up, which is the only thing that actually
 * matters and the only thing that cannot go stale.
 */
export async function waitForToken(ms = 20000): Promise<string | null> {
	if (botState === "off") return null;
	if (botState === "unavailable") return null;

	// Nothing waiting means the last one was spent or dropped. Ask now rather
	// than spending the whole timeout discovering that nobody was going to.
	if (!botToken) rearm(0);

	const until = performance.now() + ms;
	while (!botToken && performance.now() < until) {
		await new Promise((r) => setTimeout(r, 150));
	}
	const token = botToken;
	botToken = null;
	// Pre-warm the next one, so a second draw does not wait at all.
	if (token) rearm();
	return token;
}

// ---- who is asking ---------------------------------------------------------------

/**
 * A random id for this visit, so draws can be counted per sitting.
 *
 * Pricing turns on how many sprites one person makes in one go, and a total
 * cannot answer that. This is not a person and cannot be traced to one: it is
 * random, it is never sent anywhere but this site's own endpoint, and it dies
 * with the tab. sessionStorage rather than a variable so a reload does not
 * count as a new visitor.
 */
export function visitId(): string {
	try {
		const found = sessionStorage.getItem("bitwright.visit");
		if (found) return found;
		const made = crypto.randomUUID();
		sessionStorage.setItem("bitwright.visit", made);
		return made;
	} catch {
		// Private mode, or storage refused. Counting is not worth an exception.
		return "";
	}
}

// ---- the request ------------------------------------------------------------------

export interface DrawRequest {
	want: string;
	view: View;
	motion: Motion;
	/** A sprite to match, already stripped of its bookkeeping fields. */
	like?: SpriteSpec | null;
	/** An uploaded reference, already classified and scaled by the caller. */
	image?: unknown;
	/** An exact canvas, named from a fixed list. Paid only, enforced server-side. */
	size?: string | null;
}

export interface Drawn {
	spec: SpriteSpec;
	/** True when the first attempt broke a rule and was shown the error. */
	repaired: boolean;
}

/**
 * A refusal that the page can act on rather than only report.
 *
 * `outcome` is the server's own word for why, carried so the page can offer the
 * way out. Running out of an allowance is not really an error: it is the
 * product working, and it deserves a button rather than red text.
 */
export class DrawRefused extends Error {
	constructor(
		message: string,
		readonly status: number,
		readonly outcome: string | null,
	) {
		super(message);
		this.name = "DrawRefused";
	}

	/** True when making an account, or paying, is the answer. */
	get fixable() {
		return this.outcome === "no_allowance" || this.outcome === "needs_paid";
	}

	get needsPaid() {
		return this.outcome === "needs_paid";
	}

	/**
	 * The subject was turned down rather than anything going wrong.
	 *
	 * Worth its own flag so the pages can say it in a normal voice, without the
	 * red of a failure or the "try again shortly" of an outage. Nothing broke and
	 * trying again with the same words will get the same answer.
	 */
	get refused() {
		return this.outcome === "refused";
	}
}

/**
 * The sprite being written, as the page is told about it.
 *
 * The scanner's own events, plus the one thing only the draw knows: that the
 * attempt on screen has been abandoned and another has started.
 */
export type DrawWatch = StreamEvent | { type: "restart" } | { type: "thinking"; chars: number };

/**
 * The draw stopped without saying how it went.
 *
 * A dropped connection, a tab asleep, the Worker dying. Deliberately says
 * nothing about credits, unlike every other failure here: the draw may well
 * have finished and been charged for after the last byte we saw, and promising
 * otherwise would be a guess dressed up as a reassurance.
 */
const CUT_OFF = "The connection to the drawing service stopped before the sprite was finished. Check your history before drawing it again.";

/**
 * One draw. Throws with a sentence worth showing a person.
 *
 * The bot check is waited on here rather than by each caller, because forgetting
 * it produces a 403 that reads like the service is down.
 *
 * `watch` is handed each piece of the sprite as it is written, so a page can
 * draw it as it comes. Left out, this waits for the finished spec exactly as it
 * always did, which is what the welcome page wants: one sprite, no meter.
 */
export async function requestDraw(req: DrawRequest, watch?: (event: DrawWatch) => void): Promise<Drawn> {
	if (botState === "unavailable") throw new Error(BOT_BLOCKED);
	const token = await waitForToken();
	// Only when the check is switched on at all. With no keys configured the
	// endpoint does not ask for a token and neither should this.
	if (botState === "ready" && !token) throw new Error(BOT_WAITING);

	const res = await fetch("/api/draw", {
		method: "POST",
		headers: { "content-type": "application/json" },
		body: JSON.stringify({
			want: req.want,
			view: req.view,
			motion: req.motion,
			like: req.like ?? null,
			image: req.image ?? null,
			size: req.size ?? null,
			token,
			session: visitId(),
		}),
	});
	/*
	 * Everything refused before the model was asked is still an ordinary reply
	 * with a status code on it: no allowance, no token, too many at once. Only a
	 * draw that actually started streams, so the two are told apart by what came
	 * back rather than by guessing.
	 */
	if (!res.body || !(res.headers.get("content-type") ?? "").includes("text/event-stream")) {
		const body = (await res.json().catch(() => ({}))) as { error?: string; outcome?: string };
		throw new DrawRefused(body.error ?? `The service answered ${res.status}.`, res.status, body.outcome ?? null);
	}

	const reader = res.body.getReader();
	const bytes = new TextDecoder();
	const frames = new SseReader();
	let drawn: Drawn | null = null;
	let refused: DrawRefused | null = null;

	for (;;) {
		const { value, done } = await reader.read();
		if (done) break;
		for (const frame of frames.push(bytes.decode(value, { stream: true }))) {
			if (frame.event === "done") {
				const body = frame.data as { spec: SpriteSpec; repaired?: boolean };
				drawn = { spec: body.spec, repaired: Boolean(body.repaired) };
			} else if (frame.event === "error") {
				/*
				 * The status could not be sent as a status, so it is carried here.
				 *
				 * Once the first byte is out the reply is a 200 whatever happens next,
				 * and the page still has to tell an empty allowance from an outage
				 * from a subject that was turned down. Those need three different
				 * answers, and two of them are buttons.
				 */
				const body = frame.data as { error?: string; outcome?: string; status?: number };
				refused = new DrawRefused(body.error ?? "That draw did not come out.", body.status ?? 502, body.outcome ?? null);
			} else if (frame.event === "restart") {
				watch?.({ type: "restart" });
			} else if (frame.event === "thinking") {
				// A count of characters worked out, never the characters. The page
				// shows that something is happening; what it is stays where it was.
				watch?.({ type: "thinking", chars: Number((frame.data as { chars?: unknown }).chars ?? 0) });
			} else {
				watch?.(frame.data as StreamEvent);
			}
		}
	}

	if (refused) throw refused;
	if (!drawn) throw new Error(CUT_OFF);
	counted("drawn");
	return drawn;
}

// ---- the shelf, on disk -------------------------------------------------------------

/**
 * The shelf survives closing the tab.
 *
 * Somebody building a set of sprites for a game works across days, and losing
 * the lot on a browser restart is not a quirk, it is the tool throwing your
 * work away.
 *
 * It is also how a sprite drawn on the welcome page is waiting on the workbench
 * when somebody clicks through, with no handover, no query string and no
 * server: both pages are the same origin reading the same key.
 */
const SHELF_KEY = "bitwright.shelf";
export const SHELF_MAX = 60;

export interface Shelved {
	label: string;
	spec: SpriteSpec;
}

export function readShelf(): Shelved[] {
	try {
		const raw = localStorage.getItem(SHELF_KEY);
		if (!raw) return [];
		const parsed = JSON.parse(raw) as Shelved[];
		if (!Array.isArray(parsed)) return [];
		// Anything unrenderable is dropped rather than allowed to break the shelf:
		// it may have been saved by an older version of the format.
		return parsed
			.filter((e) => e && typeof e.label === "string" && e.spec)
			.filter((e) => {
				try {
					renderFrames(e.spec);
					return true;
				} catch {
					return false;
				}
			});
	} catch {
		return [];
	}
}

export function writeShelf(entries: Shelved[]) {
	try {
		localStorage.setItem(SHELF_KEY, JSON.stringify(entries.slice(0, SHELF_MAX)));
	} catch {
		// Full, or storage refused. Losing the history is better than losing the sprite.
	}
}

/** Put a freshly drawn sprite at the front. */
export function pushShelf(spec: SpriteSpec, label: string) {
	writeShelf([{ label, spec }, ...readShelf()]);
}

/**
 * Start a purchase and hand back where to send the browser.
 *
 * Shared by the price list and the account page so there is one description of
 * how buying works. The pack is named and never priced: a price arriving from a
 * browser is a price somebody can edit, so the server reads it from its own
 * table.
 *
 * Throws with the server's own words when it refuses, because "not signed in"
 * and "there is no pack by that name" need different reactions from the person
 * reading them.
 */
export async function checkoutUrl(packId: string): Promise<string> {
	const res = await fetch("/api/checkout", {
		method: "POST",
		headers: { "content-type": "application/json" },
		body: JSON.stringify({ pack: packId }),
	});
	const body = (await res.json().catch(() => ({}))) as { url?: string; error?: string };
	if (!res.ok || !body.url) throw new Error(body.error ?? "That could not be started.");
	return body.url;
}
